import React, { useState, useEffect, useContext } from 'react';
import { Redirect } from 'react-router';
import axios from 'axios';
import { alpha, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import DataGrid from 'app/components/DataGrid/CustomDataGrid';
import { airSeaQuoteColumns, airSeaQuoteVisibilityKey, airSeaQuoteVisibilityModel } from 'app/components/DataGrid/GridSettings/airSeaQuoteGridSettings';
import { bookingRowUrlPattern, idField, quoteRowUrlPattern } from 'app/components/DataGrid/GridSettings/commonSettings';
import { airSeaBookingColumns, airSeaBookingVisibilityKey, airSeaBookingVisibilityModel } from 'app/components/DataGrid/GridSettings/airSeaBookingGridSettings';
import Journey from 'app/components/Journey/Journey';
import { LoginStatusContext } from 'app/context/LoginStatusContext';
import Button from '@mui/material/Button';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { red } from '@mui/material/colors';
import DashboardAnalytics from 'app/components/Analytics/DashboardAnalytics';
import { analyticsWidgetModel, analyticsDisplayModel, analyticsDisplayModelVisibilityKey, IAnalyticsDisplayModel } from 'app/components/Analytics/AnalyticsModel';
import { getJSONFromLocalStorage } from 'app/util/localStorageUtils';
import LoadingSpinner from './LoadingSpinner';
import useDashboardData from './useDashboardData';
import { ShipmentDirection } from 'app/types/enums';
import { mapSettingsModel } from 'app/components/MapSettings/MapSettingsModel';
import DashboardMap from './DashboardMap';

const RedSwitch = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-switchBase.Mui-checked': {
        color: red[600],
        '&:hover': {
            backgroundColor: alpha(red[600], theme.palette.action.hoverOpacity),
        },
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: red[600],
    },
}));

//React export function used to display the homepage information.
export default function Dashboard() {

    const [_UserSystemKey] = useState(sessionStorage.getItem('_UserSystemKey'));
    const [loadingMessage, setLoadingMessage] = useState("Loading Europa Data...");
    const { isLoggedIn, setIsLoggedIn } = useContext(LoginStatusContext);
    const [showQuotes, setShowQuotes] = useState(false);
    const [showBookings, setShowBookings] = useState(false);
    const [showDashboardSettings, setShowDashboardSettings] = useState(false);
    const [widgetSettings, setWidgetSettings] = useState(analyticsWidgetModel);
    const [widgetSettingsLocalStorage, setWidgetSettingsLocalStorage] = useState<IAnalyticsDisplayModel[] | null>(analyticsDisplayModel);
    const [filterDashboardBookings, setFilterDashboardBookings] = useState([
        {
            type: 'import',
            isDisplay: false,
            isSelected: false
        },
        {
            type: 'export',
            isDisplay: false,
            isSelected: false
        }
    ]);
    const [mapSettings, setMapSettings] = useState(mapSettingsModel);
    const dashboardData = useDashboardData();

    useEffect(() => {
        const getValidateUserSession = () => {
            axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/login/ValidateUserSession`)
                    .then(response => {
                        if (response.data === '0') {
                            setIsLoggedIn(false);
                            sessionStorage.setItem('_LogOutInMins', '0');
                        }
                        else {
                            sessionStorage.setItem('_LogOutInMins', response.data);

                            let logOutInMins = parseInt(sessionStorage.getItem('_LogOutInMins') || '');

                            if (!Number.isNaN(logOutInMins) && logOutInMins > 0) {
                                const timer = setTimeout(() => {
                                    setIsLoggedIn(false);
                                    sessionStorage.setItem('_LogOutInMins', '0');
                                }, logOutInMins * 60 * 1000);
                                return () => clearTimeout(timer);
                            }
                        }
                    })
        }

        getValidateUserSession();
    }, [setIsLoggedIn]);

    useEffect(() => {

        const timeout = setTimeout(() => {
          setLoadingMessage("Loading tracking information...");
        }, 3000);
    
        return () => clearTimeout(timeout);
    }, []);


    useEffect(() => {
        const widgetSettingsLocalStorage: IAnalyticsDisplayModel[] | null = getJSONFromLocalStorage<IAnalyticsDisplayModel[]>(analyticsDisplayModelVisibilityKey || null);
        if (widgetSettingsLocalStorage && widgetSettings) {
            setWidgetSettingsLocalStorage(widgetSettingsLocalStorage);
            widgetSettings.map((widgetSetting) => {
                const widget = widgetSettingsLocalStorage.find((widget) => {
                    return widget?.widgetId === widgetSetting.widgetId
                });
                if (widget) {
                    widget.widgetId = widgetSetting.widgetId;
                    widget.parentId = widgetSetting.parentId;
                    widgetSetting.isDisplay = widget.isDisplay;
                }
            });
        }

    }, [_UserSystemKey, widgetSettings]);

    if (!isLoggedIn) {
        return <Redirect from="/LeoSeaPortal" to="/LeoSeaPortal/login" />;
    }

    const toggleQuoteButtonOnClick = () => {
        dashboardData.fetchQuoteData();
        setShowQuotes(!showQuotes);
        setShowBookings(false);
        setShowDashboardSettings(false);
    }

    const toggleBookingButtonOnClick = () => {
        dashboardData.fetchBookingData();
        setShowBookings(!showBookings)
        setShowQuotes(false);
        setShowDashboardSettings(false)
    }

    const toggleDashboardSettingButtonOnClick = () => {
        setShowDashboardSettings(!showDashboardSettings)
        setShowQuotes(false);
        setShowBookings(false);
    }

    const closeGrid = (event) => {
        if (!event.relatedTarget || (!event.relatedTarget.outerHTML.includes("dashboardGrid")
            && !event.relatedTarget.outerHTML.includes("MuiDataGrid")
            && !event.relatedTarget.outerHTML.includes("dashboardAnalytics"))) {
            setShowQuotes(false);
            setShowBookings(false);
            setShowDashboardSettings(false);
        }
    }

    const searchData = (fromDate, toDate) => {
        dashboardData.fetchSearchData(fromDate, toDate);
    }

    const handleChangeWidgetDisplay = (widgetId) => {

        if (widgetSettings === null) {
            return;
        }
        let newWidgetSettings = [...widgetSettings];

        let index = newWidgetSettings.findIndex((widgetSetting) => {
            return widgetSetting.widgetId === widgetId;
        });

        var toggle = !newWidgetSettings[index].isDisplay;
        newWidgetSettings[index].isDisplay = toggle;
        newWidgetSettings.filter((ws) => {
            return ws.parentId === newWidgetSettings[index].widgetId;
        }).map((ws) => { ws.isDisplay = toggle });

        setWidgetSettings(newWidgetSettings);

        if (widgetSettingsLocalStorage) {
            widgetSettingsLocalStorage[index].isDisplay = toggle;
            widgetSettingsLocalStorage.filter((ws) => {
                return ws.parentId === widgetSettingsLocalStorage[index].widgetId;
            }).map((ws) => { ws.isDisplay = toggle });
            setWidgetSettingsLocalStorage([...widgetSettingsLocalStorage]);
            localStorage.setItem(analyticsDisplayModelVisibilityKey, JSON.stringify(widgetSettingsLocalStorage));
        }
    };

    const handleToggleWidgetDisplayAll = (isChecked) => {

        if (widgetSettings === null) {
            return;
        }
        let newWidgetSettings = [...widgetSettings];

        newWidgetSettings.forEach((newWidgetSetting) => {
            newWidgetSetting.isDisplay = isChecked;
        });

        setWidgetSettings(newWidgetSettings);

        if (widgetSettingsLocalStorage) {
            widgetSettingsLocalStorage.forEach((widgetSetting) => {
                widgetSetting.isDisplay = isChecked;
            });
            setWidgetSettingsLocalStorage([...widgetSettingsLocalStorage]);
            localStorage.setItem(analyticsDisplayModelVisibilityKey, JSON.stringify(widgetSettingsLocalStorage));
        }
    };

    const handleMapSettings = (mapSettingId) => {

        if (mapSettings === null) {
            return;
        }
        let newMapSettings = [...mapSettings];

        let index = newMapSettings.findIndex((mapSetting) => {
            return mapSetting.mapSettingId === mapSettingId;
        });

        newMapSettings[index].isDisplay = !newMapSettings[index].isDisplay;
        setMapSettings(newMapSettings);
    };

    if (dashboardData.initialDataLoading) {
        return (
            <LoadingSpinner message={loadingMessage} />
        )
    }

    if (!dashboardData.initialDataLoading && dashboardData.error) {
        return (
            <Typography>{dashboardData.error}</Typography>
        )
    }

    //Render method used to render the content of the page.
    return (
        <div>
            <DashboardAnalytics tableData={dashboardData} searchData={searchData} widgetSettings={widgetSettings} widgetSettingsLocalStorage={widgetSettingsLocalStorage} />

            <div onBlur={closeGrid}>
                <div style={{ position: 'absolute', zIndex: '1', marginTop: '100px' }} >
                    <Button
                        sx={{
                            width: '90px', height: '60px', backgroundColor: showQuotes ? '#2059A7' : 'rgba(32, 89, 167, 0.5)', '&:hover': {
                                backgroundColor: '#2059A7'
                            }
                        }}
                        variant='contained'
                        style={{ position: 'absolute', zIndex: '1' }}
                        onClick={toggleQuoteButtonOnClick}
                    >
                        <FactCheckOutlinedIcon
                            sx={{ fontSize: 'xx-large' }} />
                    </Button>
                </div>
                <div style={{
                    position: 'absolute', zIndex: '1', backgroundColor: 'rgba(255, 255, 255, 0.5)', width: '70%', marginTop: '100px', marginLeft: '190px'
                }}>
                    {showQuotes && dashboardData.quoteDataLoading && (
                        <LoadingSpinner message={loadingMessage} />
                    )}
                    {showQuotes && !dashboardData.quoteDataLoading && !dashboardData.error && (
                        <>
                            <DataGrid
                                idField={idField}
                                data={dashboardData.quoteData ?? []}
                                columns={airSeaQuoteColumns}
                                rowClickUrlPattern={quoteRowUrlPattern}
                                backgroundColour={'#2059A7'}
                                visibilityModelKey={airSeaQuoteVisibilityKey}
                                defaultVisibilityModel={airSeaQuoteVisibilityModel}
                                hideFooter
                            />
                            <Typography mt={2}>
                                <em>* Currency exchange rates are calculated on the date of quotation and are subject to change.</em>
                            </Typography>
                        </>
                    )}
                    {showQuotes && !dashboardData.quoteDataLoading && dashboardData.error && (<Typography>{dashboardData.error}</Typography>)}
                </div>
            </div>

            <div onBlur={closeGrid}>
                <div style={{ position: 'absolute', zIndex: '1', marginTop: '100px' }} >
                    <Button
                        sx={{
                            width: '90px', height: '60px', backgroundColor: showBookings ? '#1DB35A' : 'rgba(45, 179, 74, 0.5)', '&:hover': {
                                backgroundColor: '#1DB35A'
                            }
                        }}
                        variant='contained'
                        style={{ position: 'absolute', zIndex: '1', marginTop: '70px' }}
                        onClick={toggleBookingButtonOnClick}>
                        <AutoStoriesIcon
                            sx={{ fontSize: 'xx-large' }} />
                    </Button>
                    {showBookings && (
                        <>
                            <Button
                                id="dashboardGrid"
                                sx={{
                                    width: '90px', height: '30px', backgroundColor: dashboardData.bookingFilter === ShipmentDirection.Import ? '#1DB35A' : 'rgba(45, 179, 74, 0.5)', '&:hover': {
                                        backgroundColor: '#1DB35A'
                                    }
                                }}
                                variant='contained'
                                style={{ position: 'absolute', zIndex: '1', marginTop: '70px', marginLeft: '90px' }}
                                onClick={() => dashboardData.setBookingFilter(ShipmentDirection.Import)}>
                                Import
                            </Button>
                            <Button
                                id="dashboardGrid"
                                sx={{
                                    width: '90px', height: '30px', backgroundColor: dashboardData.bookingFilter === ShipmentDirection.Export ? '#1DB35A' : 'rgba(45, 179, 74, 0.5)', '&:hover': {
                                        backgroundColor: '#1DB35A'
                                    }
                                }}
                                variant='contained'
                                style={{ position: 'absolute', zIndex: '1', marginTop: '100px', marginLeft: '90px' }}
                                onClick={() => dashboardData.setBookingFilter(ShipmentDirection.Export)}>
                                Export
                            </Button>
                        </>
                    )}

                </div>

                <div style={{
                    position: 'absolute', zIndex: '1', backgroundColor: 'rgba(255, 255, 255, 0.5)', width: '70%', marginTop: '100px', marginLeft: '190px'}}>
                    {showBookings && dashboardData.bookingDataLoading && (
                        <LoadingSpinner message={loadingMessage} />
                    )}
                    {showBookings && !dashboardData.bookingDataLoading && !dashboardData.error && (
                        <>
                            <DataGrid
                                idField={idField}
                                data={dashboardData.filteredBookingData ?? []}
                                columns={airSeaBookingColumns}
                                rowClickUrlPattern={bookingRowUrlPattern}
                                excludeCells={['isFavourite']}
                                backgroundColour={'#1DB35A'}
                                visibilityModelKey={airSeaBookingVisibilityKey}
                                defaultVisibilityModel={airSeaBookingVisibilityModel}
                                hideFooter
                            />

                        </>
                    )}
                    {showBookings && !dashboardData.bookingDataLoading && dashboardData.error && (<Typography>{dashboardData.error}</Typography>)}
                </div>
            </div>

            <div onBlur={closeGrid}>
                <div style={{ position: 'absolute', zIndex: '1', marginTop: '100px' }} >
                    <Button
                        sx={{
                            width: '90px', height: '60px', backgroundColor: showDashboardSettings ? '#ef2927' : 'rgba(239,41,39, 0.5)', '&:hover': {
                                backgroundColor: '#ef2927'
                            }
                        }}
                        variant='contained'
                        style={{ position: 'absolute', zIndex: '1', marginTop: '140px' }}
                        onClick={toggleDashboardSettingButtonOnClick}>
                        <SettingsIcon
                            sx={{ fontSize: 'xx-large' }} />
                    </Button>
                </div>
                <div style={{
                    position: 'absolute', zIndex: '1', backgroundColor: 'rgba(255, 255, 255, 0.5)', width: '70%', marginTop: '100px', marginLeft: '190px'
                }}>
                    {showDashboardSettings && (
                        <><FormControl>
                            <h3 style={{
                                color: '#ef2927'
                            }}>Analytics</h3>
                            <FormGroup>
                                <FormControlLabel
                                    control={<RedSwitch id="dashboardAnalytics" checked={widgetSettings.some(widgetSetting => widgetSetting.isDisplay === true)} onChange={(e) => handleToggleWidgetDisplayAll(e.target.checked)} name={'All'} />}
                                    label={'All'} />
                            </FormGroup>
                            <FormGroup>
                                {widgetSettings.map(widget => <FormControlLabel
                                    control={<RedSwitch id="dashboardAnalytics" checked={widget.isDisplay} onChange={() => handleChangeWidgetDisplay(widget.widgetId)} name={`${widget.label} ${widget.bookingType}`} />}
                                    label={`${widget.label} ${widget.bookingType}`} />
                                )}
                            </FormGroup>
                        </FormControl><FormControl style={{ marginLeft: '55px' }}>
                                <h3 style={{
                                    color: '#ef2927'
                                }}>Map Settings</h3>
                                <FormGroup>
                                    {mapSettings.map(mapSetting => <FormControlLabel
                                        control={<RedSwitch id="dashboardAnalytics" checked={mapSetting.isDisplay} onChange={() => handleMapSettings(mapSetting.mapSettingId)} name={`${mapSetting.label}`} />}
                                        label={`${mapSetting.label}`} />
                                    )}
                                </FormGroup>
                            </FormControl></>
                    )}
                </div>
            </div>
            <DashboardMap dashboardData={dashboardData.initialData} mapSettings={mapSettings} />
        </div>
    );
}
